import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { Base64 } from 'js-base64';
import { DataService, ItineraryauthService, SearchUrlService } from '../services/index';
import { Banner } from '../interfaces/home-interface';
import { HomeService } from '../services/home.service';
import { FlightSearchAirport } from '../shared/flight-search-bar/flight-search-bar.component';

const NAIROBI: FlightSearchAirport = { name: 'Jomo Kenyatta International', city: 'Nairobi', country: 'Kenya', iata: 'NBO' };

/** Destination cards on the home page, keyed by the number passed to `clickCity`. */
const DESTINATION_CARDS: Record<number, FlightSearchAirport> = {
  1: { name: 'Mombasa Moi International Airport', city: 'Mombasa', country: 'Kenya', iata: 'MBA' },
  2: { name: 'Kisumu Airport', city: 'Kisumu', country: 'Kenya', iata: 'KIS' },
  3: { name: 'Ukunda Airstrip', city: 'Ukunda', country: 'Kenya', iata: 'UKA' },
  4: { name: 'Eldoret International Airport', city: 'Eldoret', country: 'Kenya', iata: 'EDL' },
  5: { name: 'Amsterdam Airport Schiphol', city: 'Amsterdam', country: 'Netherlands', iata: 'AMS' },
  6: { name: 'London Heathrow Airport', city: 'London', country: 'United Kingdom', iata: 'LHR' },
  7: { name: 'John F Kennedy International Airport', city: 'New York', country: 'United States', iata: 'JFK' },
  8: { name: 'Leonardo da Vinci–Fiumicino Airport', city: 'Rome', country: 'Italy', iata: 'FCO' }
};

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  banners: Banner[] = [];
  webflightBanners: any[] = [];
  mobileflightBanners: any[] = [];
  currentWebIndex = 0;
  currentMobileIndex = 0;

  private intervalId: any;

  constructor(
    private service: HomeService,
    private dataService: DataService,
    private router: Router,
    private auth: ItineraryauthService,
    private searchUrl: SearchUrlService,
    private zone: NgZone
  ) {}

  /** Desktop/tablet: the search widget is pinned under the header and renders condensed. */
  isSearchStuck = false;
  @ViewChild('searchSentinel') private searchSentinel?: ElementRef<HTMLElement>;
  private readonly navbarHeight = 64;

  /**
   * The page scrolls the <body> element, whose scroll events do not bubble, so listen in
   * the capture phase on the document. The sentinel sits just above the widget and never
   * changes size, so the pinned state cannot flicker as the widget condenses.
   */
  private readonly onAnyScroll = (): void => {
    const sentinel = this.searchSentinel?.nativeElement;
    const dock = sentinel?.nextElementSibling as HTMLElement | null;
    if (!sentinel || !dock || !dock.offsetParent) {
      return;
    }
    const pullUp = parseFloat(getComputedStyle(dock).marginTop) || 0;
    const stuck = sentinel.getBoundingClientRect().top + pullUp < this.navbarHeight;
    if (stuck !== this.isSearchStuck) {
      this.zone.run(() => (this.isSearchStuck = stuck));
    }
  };

  ngOnInit(): void {
    document.addEventListener('scroll', this.onAnyScroll, { capture: true, passive: true });
    this.loadBanners();
    this.getTokenInit();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onAnyScroll, { capture: true } as any);
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  loadBanners() {
    this.service.getBanners().subscribe({
      next: (res) => this.filterBanners(res, false),
      error: (error) => console.error('Error loading web banners:', error)
    });

    this.service.getMobileBanners().subscribe({
      next: (res) => this.filterBanners(res, true),
      error: (err) => console.error('Error loading mobile banners:', err)
    });
  }

  filterBanners(banners: any[], isMobile: boolean) {
    const usable = (banners || []).filter(b => b.imageUrl?.trim() !== '');
    if (isMobile) {
      this.mobileflightBanners = usable;
    } else {
      this.webflightBanners = usable;
    }

    this.currentWebIndex = 0;
    this.currentMobileIndex = 0;

    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.webflightBanners.length > 0 || this.mobileflightBanners.length > 0) {
      this.intervalId = setInterval(() => this.nextImage(), 4000);
    }
  }

  nextImage(): void {
    if (this.webflightBanners.length > 0) {
      this.currentWebIndex = (this.currentWebIndex + 1) % this.webflightBanners.length;
    }
    if (this.mobileflightBanners.length > 0) {
      this.currentMobileIndex = (this.currentMobileIndex + 1) % this.mobileflightBanners.length;
    }
  }

  getTokenInit() {
    this.auth.getToken().subscribe({ next: () => {}, error: () => {} });
  }

  /** A destination card was clicked: run a one-way search from Nairobi for tomorrow. */
  clickCity(card: number) {
    const from = NAIROBI;
    const to = DESTINATION_CARDS[card] ?? DESTINATION_CARDS[5];

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const traveldate = formatDate(tomorrow, 'yyyy-MM-dd', 'en-KE');

    const search = {
      depature_city: from.iata,
      destination_city: to.iata,
      departure_on: traveldate,
      arrival_on: traveldate,
      trip_type: 'oneway',
      adult_count: 1,
      child_count: 0,
      infant_count: 0,
      travel_class: 'economy',
      from_data: from,
      destination_data: to,
      returning: false
    };

    this.dataService.changeMessage(Base64.encode(JSON.stringify(search)));
    const routeSlug = this.searchUrl.buildRouteSlug(from.city, to.city);
    this.router.navigate(['/booking', routeSlug], {
      queryParams: this.searchUrl.buildQueryParams(search)
    });
  }
}
